import { marked } from "marked";

/**
 * Configures marked with secure settings to prevent XSS attacks
 */
marked.setOptions({
  // Disable raw HTML to prevent XSS
  mangle: false,
  headerIds: false,
});

// Configure renderer to disable raw HTML
const renderer = new marked.Renderer();

// Override HTML rendering to return empty string (blocks all raw HTML)
renderer.html = () => "";

/**
 * Parses Markdown text to safe HTML
 * Supports: bold, italic, underline (via HTML), links, lists, line breaks, paragraphs
 * Strips any raw HTML tags for security
 *
 * @param text - Markdown formatted text
 * @returns Sanitized HTML string
 */
export function parseMarkdown(text: string): string {
  if (!text) {
    return "";
  }

  try {
    // Parse markdown with secure renderer
    const html = marked.parse(text, { renderer }) as string;
    return html;
  } catch (error) {
    console.error("Markdown parsing error:", error);
    // Fallback to plain text with line breaks preserved
    return text.replace(/\n/g, "<br>");
  }
}

/**
 * Strips markdown formatting from text for plain text previews
 * Useful for product card previews where formatting isn't needed
 *
 * @param text - Markdown formatted text
 * @returns Plain text without markdown syntax
 */
export function stripMarkdown(text: string): string {
  if (!text) {
    return "";
  }

  // Remove markdown syntax
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1") // Bold
    .replace(/\*([^*]+)\*/g, "$1") // Italic
    .replace(/__([^_]+)__/g, "$1") // Underline
    .replace(/_([^_]+)_/g, "$1") // Alt italic
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Links
    .replace(/^#+\s+/gm, "") // Headers
    .replace(/^[-*]\s+/gm, "") // Lists
    .replace(/^\d+\.\s+/gm, ""); // Numbered lists
}
